import {useCallback, useEffect, useRef, useState} from 'react';

import {type BrowseCourse} from '@udemy/browse-course';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {
    type RecommendationContextSection,
    type RecommendationUnit,
    type RelatedCategoryItem,
    extractCategoryAndSubcategoryItems,
    extractCourseBundles,
    fetchRecommendationsContext,
    fetchRecommendationSections,
    flattenRecommendationSections,
    isCourseRecommendation,
    isFeaturedCourseRecommendationUnit,
    isSkillsHubSection,
    RecommendationContextIds,
    RecommendationSourceObjectTypes,
    useToDiscoveryUnits,
} from '@udemy/react-recommendations';
import {udSentry} from '@udemy/sentry';

import {
    CursorMap,
    extractCursors,
    fetchMoreTrackedItems,
    toTrackedDiscoveryUnits,
} from 'src/lib/recommendations/recommendation-unit-helpers';

interface UseTopicRecommendationsParams {
    topicId: string;
    pageType: string;
    // TODO: CVR-1878 - Remove when recommendation units migration is complete
    isEnabled?: boolean;
    shouldFetchBundle?: boolean;
    requestedSectionCount?: number;
    requestedItemCountPerUnit?: number;
}

export type OnLoadMoreByTitle = (title: string) => Promise<Partial<DiscoveryUnit>>;

interface UseTopicRecommendationsResult {
    units: Partial<DiscoveryUnit>[];
    bundleCourses?: BrowseCourse[];
    relatedCategories?: RelatedCategoryItem[];
    isLoading: boolean;
    error: unknown;
    onLoadMore: OnLoadMoreByTitle;
}

const DEFAULT_SECTION_COUNT = 7;
const DEFAULT_ITEM_COUNT = 12;

const getCourseIdFromUnit = (unit?: RecommendationUnit): string | undefined => {
    const item = unit?.items?.[0];
    if (!item || !isCourseRecommendation(item)) return undefined;
    return item.course?.id ?? undefined;
};

const extractBundleSourceCourseId = (
    sections: RecommendationContextSection[],
): string | undefined => {
    const skillsHub = sections.find(isSkillsHubSection);
    const skillsHubCourseId =
        skillsHub && 'units' in skillsHub
            ? skillsHub.units.map(getCourseIdFromUnit).find(Boolean)
            : undefined;
    if (skillsHubCourseId) return skillsHubCourseId;

    const units = flattenRecommendationSections(sections);
    return getCourseIdFromUnit(units.find(isFeaturedCourseRecommendationUnit));
};

const fetchBundleCourses = async (
    sourceCourseId: string,
    setBundleCourses: (courses: BrowseCourse[]) => void,
    signal: AbortSignal,
    localizeItem: (item: BrowseCourse) => BrowseCourse,
) => {
    try {
        const bundleData = await fetchRecommendationSections({
            params: {
                contextId: RecommendationContextIds.CLP_BUNDLE,
                sourceObjects: [{type: RecommendationSourceObjectTypes.COURSE, id: sourceCourseId}],
            },
            signal,
        });
        const courses = extractCourseBundles(bundleData).slice(0, 2).map(localizeItem);
        if (!signal.aborted && courses.length === 2) setBundleCourses(courses);
    } catch (e) {
        if (!signal.aborted) udSentry.captureException(e);
    }
};

export function useTopicRecommendations({
    topicId,
    pageType,
    isEnabled = true,
    shouldFetchBundle = true,
    requestedSectionCount = DEFAULT_SECTION_COUNT,
    requestedItemCountPerUnit = DEFAULT_ITEM_COUNT,
}: UseTopicRecommendationsParams): UseTopicRecommendationsResult {
    const [units, setUnits] = useState<Partial<DiscoveryUnit>[]>([]);
    const [bundleCourses, setBundleCourses] = useState<BrowseCourse[]>();
    const [relatedCategories, setRelatedCategories] = useState<RelatedCategoryItem[]>();
    const [isLoading, setIsLoading] = useState(isEnabled);
    const [error, setError] = useState<unknown>(null);
    const cursors = useRef<CursorMap>(new Map());

    const {localizedItemMapper, toLocalizedDiscoveryItem} = useToDiscoveryUnits();
    const loadMoreControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!isEnabled) return;

        const controller = new AbortController();
        const {signal} = controller;

        const fetchData = async () => {
            const sourceObjects = [{type: RecommendationSourceObjectTypes.LABEL, id: topicId}];
            try {
                const [topicResult, contextualHeaderData] = await Promise.all([
                    fetchRecommendationsContext({
                        params: {
                            contextId: RecommendationContextIds.TOPIC,
                            sourceObjects,
                            requestedSectionCount,
                            requestedItemCountPerUnit,
                        },
                        signal,
                    }),
                    fetchRecommendationSections({
                        params: {
                            contextId: RecommendationContextIds.TOPIC_CONTEXTUAL_HEADER,
                            sourceObjects,
                        },
                        signal,
                    }),
                ]);

                const topicSections = topicResult.recommendationContext.sections ?? [];
                cursors.current = extractCursors(topicSections);

                if (signal.aborted) return;

                const discoveryUnits = toTrackedDiscoveryUnits(topicSections, cursors.current, {
                    itemMapper: localizedItemMapper,
                });
                setUnits(discoveryUnits);
                setRelatedCategories(extractCategoryAndSubcategoryItems(contextualHeaderData));

                if (shouldFetchBundle) {
                    const sourceCourseId = extractBundleSourceCourseId(topicSections);
                    if (sourceCourseId)
                        fetchBundleCourses(
                            sourceCourseId,
                            setBundleCourses,
                            signal,
                            toLocalizedDiscoveryItem as (item: BrowseCourse) => BrowseCourse,
                        );
                }
            } catch (e) {
                if (signal.aborted) return;
                setError(e);
                udSentry.captureException(e);
                setRelatedCategories([]);
            } finally {
                if (!signal.aborted) setIsLoading(false);
            }
        };

        fetchData();
        return () => {
            controller.abort();
            loadMoreControllerRef.current?.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- udData.request.locale/language are debug-only; including them would re-fetch after bootstrap and corrupt the accept-language header
    }, [
        isEnabled,
        shouldFetchBundle,
        topicId,
        pageType,
        requestedSectionCount,
        requestedItemCountPerUnit,
        localizedItemMapper,
        toLocalizedDiscoveryItem,
    ]);

    const onLoadMore = useCallback(
        async (title: string): Promise<Partial<DiscoveryUnit>> => {
            loadMoreControllerRef.current?.abort();
            const loadMoreController = new AbortController();
            loadMoreControllerRef.current = loadMoreController;
            const {signal} = loadMoreController;

            const cursor = cursors.current.get(title);
            if (!cursor) {
                return {items: [], remaining_item_count: 0};
            }

            const {items, nextCursor} = await fetchMoreTrackedItems(
                cursor,
                requestedItemCountPerUnit,
                localizedItemMapper,
                signal,
            );

            if (signal.aborted) return {items: [], remaining_item_count: 0};

            nextCursor ? cursors.current.set(title, nextCursor) : cursors.current.delete(title);

            return {
                items: items ?? [],
                remaining_item_count: nextCursor ? 1 : 0,
            };
        },
        [requestedItemCountPerUnit, localizedItemMapper],
    );

    return {units, bundleCourses, relatedCategories, isLoading, error, onLoadMore};
}
